import { useState } from 'react';
import { AktiveBingoaufgaben } from '../components/AktiveBingoaufgaben';
import { UmfrageStand } from '../components/UmfrageStand';
import { Ansagen } from '../components/Ansagen';
import { ChallengeBewertung } from '../components/ChallengeBewertung';
import { SpielerTwitch } from '../components/SpielerTwitch';
import { BingoBoard } from '../components/BingoBoard';
import { Punktestand } from '../components/Punktestand';
import { Zeitanzeige } from '../components/Zeitanzeige';
import { api, ApiError } from '../lib/api';
import { useLive } from '../lib/live';
import type { PlayerState } from '../lib/types';

/**
 * Die Ansicht eines Spielers.
 *
 * Geklickt wird direkt auf das Board. Ob das erlaubt ist, entscheidet der Server – die Oberfläche
 * sperrt zwar vorsorglich, wenn gerade nicht gespielt werden kann, verlässt sich aber nicht
 * darauf. Kommt eine Ablehnung zurück, wird sie im Klartext angezeigt, statt den Klick stillschweigend
 * verschwinden zu lassen.
 */
export function PlayerView({ lobbyId, onLogout }: { lobbyId: string; onLogout: () => void }) {
  const [aktivesTeam, setAktivesTeam] = useState<string | undefined>(undefined);
  const [meldung, setMeldung] = useState<string | null>(null);
  const live = useLive<PlayerState>({ lobbyId, ...(aktivesTeam ? { teamId: aktivesTeam } : {}) });

  if (live.fehler) {
    return (
      <main className="page">
        <p className="notice">{live.fehler}</p>
        <button type="button" onClick={onLogout}>
          Abmelden
        </button>
      </main>
    );
  }

  if (!live.state) {
    return (
      <main className="page">
        <p className="muted">verbindet …</p>
      </main>
    );
  }

  const { lobby, teams, myTeamIds, board, scores, timer } = live.state;
  const teamId = aktivesTeam && myTeamIds.includes(aktivesTeam) ? aktivesTeam : myTeamIds[0];
  const spielbar = timer.phase === 'RUNNING' && !!teamId;

  async function umschalten(cellIndex: number) {
    if (!teamId) return;
    setMeldung(null);
    try {
      await api.toggleField(lobby.id, teamId, cellIndex);
    } catch (err) {
      setMeldung(err instanceof ApiError ? err.message : 'Das hat nicht geklappt.');
    }
  }

  return (
    <main className="page player">
      <header className="page-head">
        <div>
          <p className="muted small">
            {lobby.name}
            {lobby.round ? ` · Runde ${lobby.round}` : ''}
            {live.status !== 'live' ? ' · Verbindung unterbrochen' : ''}
          </p>
          <h1>Bingo</h1>
        </div>
        <button type="button" className="ghost" onClick={onLogout}>
          Abmelden
        </button>
      </header>

      <Zeitanzeige timer={timer} />

      {myTeamIds.length > 1 && (
        <div className="team-switch">
          <span className="muted small">Dein Team:</span>
          {myTeamIds.map((id) => {
            const team = teams.find((t) => t.id === id);
            if (!team) return null;
            return (
              <button
                key={id}
                type="button"
                className={id === teamId ? 'team-chip active' : 'team-chip'}
                style={id === teamId ? { background: team.color } : { borderColor: team.color }}
                onClick={() => {
                  setAktivesTeam(id);
                  live.wechsleTeam(id);
                }}
              >
                {team.name}
              </button>
            );
          })}
        </div>
      )}

      {!spielbar && timer.phase !== 'RUNNING' && (
        <p className="notice calm">
          {timer.phase === 'IDLE'
            ? 'Die Runde hat noch nicht begonnen.'
            : timer.phase === 'PAUSED'
              ? 'Die Runde ist gerade pausiert.'
              : timer.phase === 'EXPIRED'
                ? 'Die Zeit ist abgelaufen.'
                : 'Die Runde ist beendet.'}
        </p>
      )}

      {meldung && <p className="notice">{meldung}</p>}

      <div className="wc-bingo-mit-liste">
      <div className="wc-bingo-hauptbereich">
      {board ? (
        <BingoBoard
          board={board}
          teams={teams}
          scores={scores}
          istAnklickbar={spielbar}
          onToggle={(index) => void umschalten(index)}
        />
      ) : (
        <p className="muted">Es wurde noch kein Board erzeugt.</p>
      )}

      </div>
      <aside className="wc-bingo-rechts"><AktiveBingoaufgaben board={board} teamId={teamId} teams={teams} /></aside>
      </div>
      <section className="panel">
        <h2>Punkte</h2>
        <Punktestand teams={teams} scores={scores} />
      </section>

      <UmfrageStand poll={live.state.poll} teams={teams} />
      <ChallengeBewertung lobbyId={lobbyId} teamIds={myTeamIds} teams={teams} />
      <SpielerTwitch lobbyId={lobbyId} />
      <Ansagen stand={{ timer, scores, teams, poll: live.state.poll }} />
    </main>
  );
}
